import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { brandApi } from "../lib/brand-api";
import { Button } from "../components/ui/Button";
import { useToast } from "../components/ui/Toast";

// PR #65 — Per-location chain toggle UI. Reads /api/brand/:slug/chains/:chainId
// and lets the brand include/exclude individual C10/C12 storefronts, add a
// missing canonical location, and archive/unarchive the chain.

interface ChainOverview {
  tenant_chain_id: string;
  chain_name: string;
  source_chain_id: string | null;
  source_chain_name: string | null;
  archived_at: string | null;
  canonical_location_count: number;
  included_member_count: number;
  cm_direct_count: number;
  wtb_confirmed_count: number;
  adspend_confirmed_count: number;
  account_only_license_count: number;
}

interface MemberRow {
  tenant_store_id: string;
  source: string | null;
  status: string;
  confirmed_by_wtb_at: string | null;
  confirmed_by_adspend_at: string | null;
  excluded_at: string | null;
  added_at: string | null;
  license_number: string | null;
  display_name: string | null;
  cm_display_name: string | null;
  origin: string | null;
  dba_name: string | null;
  legal_name: string | null;
  license_type: string | null;
  premise_city: string | null;
  premise_street: string | null;
}

interface AccountLicense {
  tenant_chain_account_license_id: string;
  license_number: string;
  license_type: string;
  source: string;
  added_at: string;
}

interface Candidate {
  license_number: string;
  dba_name: string | null;
  legal_name: string | null;
  license_type: string | null;
  premise_city: string | null;
  premise_street: string | null;
}

const SOURCE_BADGE: Record<string, { label: string; cls: string }> = {
  "cm-direct": { label: "CM-direct", cls: "bg-violet-50 text-violet-700 border-violet-200" },
  "cm-expanded": { label: "CM-expanded", cls: "bg-gray-100 text-gray-600 border-gray-200" },
  "user-added": { label: "added manually", cls: "bg-purple-50 text-purple-700 border-purple-200" },
  "wtb-confirmed": { label: "WTB", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  "adspend-confirmed": { label: "ad-spend", cls: "bg-amber-50 text-amber-700 border-amber-200" },
};

function memberName(m: MemberRow): string {
  return m.dba_name || m.display_name || m.cm_display_name || m.legal_name || m.license_number || m.tenant_store_id.slice(0, 8);
}

function isIncluded(m: MemberRow): boolean {
  return m.status !== "user-excluded";
}

export default function BrandChainDetail({ slug, chainId }: { slug: string; chainId: string }) {
  const toast = useToast();
  const base = `/api/brand/${encodeURIComponent(slug)}/chains/${encodeURIComponent(chainId)}`;

  const [chain, setChain] = useState<ChainOverview | null>(null);
  const [members, setMembers] = useState<MemberRow[]>([]);
  const [accountLicenses, setAccountLicenses] = useState<AccountLicense[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [archiving, setArchiving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await brandApi.get(base);
      setChain(r.chain || null);
      setMembers(r.members || []);
      setAccountLicenses(r.account_licenses || []);
    } catch (err) {
      toast.push(`Failed to load chain: ${(err as Error).message}`, "error");
    } finally {
      setLoading(false);
    }
  }, [base, toast]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleMember = async (m: MemberRow) => {
    const nextStatus = isIncluded(m) ? "user-excluded" : "auto-included";
    setBusyId(m.tenant_store_id);
    // Optimistic flip.
    setMembers((prev) =>
      prev.map((x) => (x.tenant_store_id === m.tenant_store_id ? { ...x, status: nextStatus } : x))
    );
    try {
      await brandApi.post(`${base}/members/${encodeURIComponent(m.tenant_store_id)}/toggle`, { status: nextStatus });
      toast.push(nextStatus === "user-excluded" ? "Location excluded." : "Location included.", "success");
      // Reload to refresh summary counts.
      load();
    } catch (err) {
      toast.push(`Toggle failed: ${(err as Error).message}`, "error");
      setMembers((prev) =>
        prev.map((x) => (x.tenant_store_id === m.tenant_store_id ? { ...x, status: m.status } : x))
      );
    } finally {
      setBusyId(null);
    }
  };

  const addLocation = async (licenseNumber: string) => {
    try {
      await brandApi.post(`${base}/members/add`, { license_number: licenseNumber });
      toast.push("Location added.", "success");
      load();
    } catch (err) {
      toast.push(`Add failed: ${(err as Error).message}`, "error");
    }
  };

  const toggleArchive = async () => {
    if (!chain) return;
    setArchiving(true);
    try {
      const action = chain.archived_at ? "unarchive" : "archive";
      await brandApi.post(`${base}/${action}`, {});
      toast.push(chain.archived_at ? "Unarchived." : "Archived.", "success");
      load();
    } catch (err) {
      toast.push(`Action failed: ${(err as Error).message}`, "error");
    } finally {
      setArchiving(false);
    }
  };

  // Group members by city for display.
  const grouped = useMemo(() => {
    const map = new Map<string, MemberRow[]>();
    for (const m of members) {
      const key = m.premise_city || "—";
      const arr = map.get(key);
      if (arr) arr.push(m);
      else map.set(key, [m]);
    }
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [members]);

  if (loading) {
    return <div className="rounded-lg border border-gray-200 bg-white p-10 text-center text-gray-500 text-sm">Loading chain…</div>;
  }
  if (!chain) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-10 text-center">
        <p className="text-gray-700 text-sm font-medium">Chain not found.</p>
        <a href={`#/brand/${encodeURIComponent(slug)}/my-chains`} className="text-accent-700 text-sm hover:underline mt-2 inline-block">
          ← Back to My Chains
        </a>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <a href={`#/brand/${encodeURIComponent(slug)}/my-chains`} className="text-xs text-gray-500 hover:text-gray-800">
          ← My Chains
        </a>
        <div className="mt-1 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">
              {chain.chain_name}
              {chain.archived_at && (
                <span className="ml-2 inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500 align-middle">
                  Archived
                </span>
              )}
            </h1>
            <div className="mt-2 flex flex-wrap gap-1.5 text-xs">
              <span className="inline-flex items-center rounded-full border border-gray-200 bg-white px-2 py-0.5 font-medium text-gray-700">
                {chain.included_member_count} included / {chain.canonical_location_count} canonical
              </span>
              {chain.cm_direct_count > 0 && (
                <span className="inline-flex items-center rounded-full border border-violet-200 bg-violet-50 px-2 py-0.5 font-medium text-violet-700">
                  {chain.cm_direct_count} CM-direct
                </span>
              )}
              {chain.wtb_confirmed_count > 0 && (
                <span className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 font-medium text-emerald-700">
                  {chain.wtb_confirmed_count} WTB-confirmed
                </span>
              )}
              {chain.adspend_confirmed_count > 0 && (
                <span className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 font-medium text-amber-700">
                  {chain.adspend_confirmed_count} ad-spend-confirmed
                </span>
              )}
            </div>
          </div>
          <Button variant={chain.archived_at ? "secondary" : "danger-ghost"} onClick={toggleArchive} disabled={archiving}>
            {chain.archived_at ? "Unarchive chain" : "Archive chain"}
          </Button>
        </div>
      </div>

      <AddLocation base={base} onAdd={addLocation} existing={members} />

      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
        {members.length === 0 ? (
          <div className="p-10 text-center text-sm text-gray-500">No canonical storefronts for this chain.</div>
        ) : (
          grouped.map(([city, list]) => (
            <div key={city}>
              <div className="bg-gray-50 px-4 py-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500 border-t border-gray-100">
                {city} <span className="font-normal text-gray-400">· {list.length}</span>
              </div>
              <ul className="divide-y divide-gray-100">
                {list.map((m) => {
                  const included = isIncluded(m);
                  const src = m.source ? SOURCE_BADGE[m.source] : null;
                  return (
                    <li key={m.tenant_store_id} className="flex items-start gap-3 px-4 py-3 hover:bg-gray-50/70">
                      <input
                        type="checkbox"
                        checked={included}
                        disabled={busyId === m.tenant_store_id}
                        onChange={() => toggleMember(m)}
                        className="mt-1 h-4 w-4 rounded border-gray-300 text-accent-600 focus:ring-accent-500/50"
                        aria-label={included ? "Exclude location" : "Include location"}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className={`font-medium ${included ? "text-gray-900" : "text-gray-400 line-through"}`}>
                            {memberName(m)}
                          </span>
                          {src && (
                            <span className={`inline-flex items-center rounded-full border px-1.5 py-0.5 text-[10px] font-medium ${src.cls}`}>
                              {src.label}
                            </span>
                          )}
                          {m.confirmed_by_wtb_at && (
                            <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-emerald-700" title="Confirmed by Where-to-Buy">
                              ✓ WTB
                            </span>
                          )}
                          {m.confirmed_by_adspend_at && (
                            <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-amber-600" title="Confirmed by ad spend">
                              ✓ ad-spend
                            </span>
                          )}
                        </div>
                        <div className="mt-0.5 text-xs text-gray-500">
                          <span className="font-mono">{m.license_number || "no license"}</span>
                          {m.premise_street ? ` · ${m.premise_street}` : ""}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))
        )}
      </div>

      {accountLicenses.length > 0 && (
        <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-900">Account-only licenses</h3>
          <p className="mt-0.5 text-xs text-gray-500">
            This account also holds {accountLicenses.length} non-storefront license
            {accountLicenses.length === 1 ? "" : "s"} (delivery / distribution). These are not chain locations.
          </p>
          <ul className="mt-3 divide-y divide-gray-100">
            {accountLicenses.map((a) => (
              <li key={a.tenant_chain_account_license_id} className="flex items-center justify-between py-2 text-sm">
                <span className="font-mono text-gray-700">{a.license_number}</span>
                <span className="text-xs text-gray-500">{a.license_type}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// "Add a missing location" autocomplete — queries /candidates and POSTs the pick.
function AddLocation({
  base,
  onAdd,
  existing,
}: {
  base: string;
  onAdd: (licenseNumber: string) => void;
  existing: MemberRow[];
}) {
  const toast = useToast();
  const [q, setQ] = useState("");
  const [results, setResults] = useState<Candidate[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const debounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  const existingLicenses = useMemo(
    () => new Set(existing.map((m) => m.license_number).filter(Boolean) as string[]),
    [existing]
  );

  useEffect(() => {
    if (debounce.current) clearTimeout(debounce.current);
    const term = q.trim();
    if (!term) {
      setResults([]);
      setOpen(false);
      return;
    }
    debounce.current = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await brandApi.get(`${base}/candidates?q=${encodeURIComponent(term)}`);
        setResults((r.rows || []).filter((c: Candidate) => !existingLicenses.has(c.license_number)));
        setOpen(true);
      } catch (err) {
        toast.push(`Search failed: ${(err as Error).message}`, "error");
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => {
      if (debounce.current) clearTimeout(debounce.current);
    };
  }, [q, base, existingLicenses, toast]);

  const pick = (c: Candidate) => {
    setQ("");
    setResults([]);
    setOpen(false);
    onAdd(c.license_number);
  };

  return (
    <div className="relative rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <label className="block text-xs font-medium text-gray-700 mb-1">Add a missing location</label>
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search this chain's canonical storefronts by name, city, or license…"
        className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent-500/50"
      />
      {open && (
        <div className="absolute left-4 right-4 z-20 mt-1 max-h-72 overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg">
          {loading ? (
            <div className="px-3 py-3 text-sm text-gray-500">Searching…</div>
          ) : results.length === 0 ? (
            <div className="px-3 py-3 text-sm text-gray-500">No unmatched storefronts found.</div>
          ) : (
            results.map((c) => (
              <button
                key={c.license_number}
                type="button"
                onClick={() => pick(c)}
                className="block w-full px-3 py-2 text-left hover:bg-gray-50"
              >
                <div className="text-sm font-medium text-gray-900">{c.dba_name || c.legal_name || c.license_number}</div>
                <div className="text-xs text-gray-500">
                  <span className="font-mono">{c.license_number}</span>
                  {c.premise_city ? ` · ${c.premise_city}` : ""}
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
